import React, { useState, useEffect } from "react";
import { DatePicker } from "./date-picker";
import { safeParseDateValue, safeFormatDateForAPI } from "utils/safe-parsing";
import { AlertTriangle } from "lucide-react";

interface SafeDatePickerProps {
  value?: string;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  onParseError?: (error: string) => void;
  showParseErrors?: boolean;
}

/**
 * A wrapper around DatePicker that safely handles invalid date values
 * and prevents crashes due to malformed date strings.
 */
export function SafeDatePicker({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  className,
  disabled,
  error,
  onParseError,
  showParseErrors = true,
  ...props
}: SafeDatePickerProps) {
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsedDate, setParsedDate] = useState<Date | undefined>(undefined);

  // Safely parse the incoming value
  useEffect(() => {
    if (!value) {
      setParsedDate(undefined);
      setParseError(null);
      return;
    }

    const parseResult = safeParseDateValue(value);

    if (parseResult.isValid && parseResult.date) {
      setParsedDate(parseResult.date);
      setParseError(null);
    } else {
      setParsedDate(undefined);
      const errorMessage = parseResult.error || "Invalid date format";
      setParseError(errorMessage);

      // Call the error callback if provided
      if (onParseError) {
        onParseError(errorMessage);
      }

      // Log error for debugging
      console.warn("SafeDatePicker: Date parsing error:", {
        value,
        error: errorMessage,
      });
    }
  }, [value, onParseError]);

  const handleDateChange = (date: Date | undefined) => {
    if (onChange) {
      onChange(date);
    }

    // Clear parse errors when user makes a change
    if (parseError) {
      setParseError(null);
    }
  };

  return (
    <div className="relative">
      <DatePicker
        value={parsedDate}
        onChange={handleDateChange}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        error={error || !!parseError}
        {...props}
      />

      {/* Show parse error if enabled and there's an error */}
      {showParseErrors && parseError && (
        <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-xs text-red-600 z-10">
          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>
            Invalid date format: {parseError}
            <br />
            <span className="text-red-500">
              Expected: MM/DD/YYYY or MM-DD-YYYY
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Hook for managing safe date field state in forms
 */
export function useSafeDateField(initialValue?: string) {
  const [value, setValue] = useState<string>(initialValue || "");
  const [parseError, setParseError] = useState<string | null>(null);

  const handleChange = (date: Date | undefined) => {
    if (date) {
      const formattedDate = safeFormatDateForAPI(date);
      if (formattedDate) {
        setValue(formattedDate);
        setParseError(null);
      } else {
        setParseError("Failed to format date. Please use MM/DD/YYYY format.");
      }
    } else {
      setValue("");
      setParseError(null);
    }
  };

  const handleParseError = (error: string) => {
    setParseError(error);
  };

  return {
    value,
    setValue,
    parseError,
    setParseError,
    handleChange,
    handleParseError,
    isValid: !parseError,
  };
}

export default SafeDatePicker;
